/**
 * Navigation Configuration
 *
 * Centralized source of truth for all navigation structures.
 * Used by: Header, MobileNav, BottomNav, Footer, CommandPalette
 *
 * SEO Optimized:
 * - Semantic link structure
 * - Clear hierarchy
 * - Descriptive labels
 * - Keyboard navigation support
 *
 * @example
 * ```tsx
 * import { NAVIGATION } from '@/lib/navigation/config';
 *
 * // Use in components
 * NAVIGATION.header.map(item => <NavLink item={item} />)
 * ```
 */

import {
  Home,
  User,
  FileText,
  Briefcase,
  Mail,
  Activity,
  Bookmark,
  Heart,
  Rss,
  Zap,
  Package,
  Boxes,
} from 'lucide-react';
import type { NavItem, NavSection, NavigationConfig } from './types';

// ============================================================================
// PRIMARY NAVIGATION (Header & Mobile)
// ============================================================================

/**
 * Core navigation items displayed in header
 * Optimized for discoverability and SEO
 */
export const HEADER_NAV: NavItem[] = [
  {
    href: '/',
    label: 'Home',
    shortLabel: 'Home',
    icon: Home,
    shortcut: 'g h',
    description: 'Return home',
    exactMatch: true,
  },
  {
    href: '/ai',
    label: 'DCYFR AI',
    shortLabel: 'DCYFR AI',
    icon: Zap,
    shortcut: 'g i',
    description: 'DCYFR AI — open-source multi-provider agent framework',
  },
  {
    href: '/open-source',
    label: 'Open Source',
    shortLabel: 'Open Source',
    icon: Package,
    shortcut: 'g o',
    description: 'npm packages, the component registry, and the plugin ecosystem',
  },
  {
    href: '/blog',
    label: 'Blog',
    shortLabel: 'Blog',
    icon: FileText,
    shortcut: 'g b',
    description: 'Read articles on security and development',
  },
  {
    href: '/work',
    label: 'Work',
    shortLabel: 'Work',
    icon: Briefcase,
    shortcut: 'g w',
    description: 'Explore our portfolio and client work',
  },
  {
    href: '/about',
    label: 'About',
    shortLabel: 'About',
    icon: User,
    shortcut: 'g a',
    description: 'Learn about us and our team',
  },
  {
    href: '/contact',
    label: 'Contact',
    shortLabel: 'Contact',
    icon: Mail,
    shortcut: 'g c',
    description: 'Get in touch with us',
  },
];

// ============================================================================
// BLOG SUB-NAVIGATION
// ============================================================================

export const BLOG_NAV: NavItem[] = [
  {
    href: '/blog',
    label: 'All Posts',
    description: 'Browse our blog articles',
    exactMatch: true,
  },
  {
    href: '/blog/series',
    label: 'Blog Series',
    description: 'In-depth multi-part articles',
  },
  {
    href: '/blog?category=AI',
    label: 'Artificial Intelligence',
    description: 'Artificial intelligence and development',
  },
  {
    href: '/blog?category=Web',
    label: 'Web Development',
    description: 'Modern full-stack web development',
  },
  {
    href: '/blog?category=DevSecOps',
    label: 'DevSecOps',
    description: 'Cybersecurity and secure development',
  },
];

// ============================================================================
// WORK/PORTFOLIO SUB-NAVIGATION
// ============================================================================

export const WORK_NAV: NavItem[] = [
  {
    href: '/work',
    label: 'All Projects',
    description: 'View complete portfolio',
    exactMatch: true,
  },
  {
    href: '/work?category=code',
    label: 'Code',
    description: 'Open-source code and development projects',
  },
  {
    href: '/work?category=startup',
    label: 'Startups',
    description: 'Startup ventures and product work',
  },
  {
    href: '/work?category=nonprofit',
    label: 'Nonprofits',
    description: 'Nonprofit and community contributions',
  },
];

// ============================================================================
// MOBILE NAVIGATION (Drawer)
// ============================================================================

/**
 * Mobile navigation sections with grouping
 * Optimized for touch targets and quick access
 */
export const MOBILE_NAV_SECTIONS: NavSection[] = [
  {
    id: 'main',
    label: 'Main Navigation',
    description: 'Primary pages',
    items: [
      {
        href: '/',
        label: 'Home',
        icon: Home,
        description: 'Return to homepage',
        exactMatch: true,
      },
      {
        href: '/ai',
        label: 'DCYFR AI',
        icon: Zap,
        description: 'DCYFR AI — open-source multi-provider agent framework',
      },
      {
        href: '/open-source',
        label: 'Open Source',
        icon: Package,
        description: 'npm packages, the component registry, and the plugin ecosystem',
      },
      {
        href: '/blog',
        label: 'Blog',
        icon: FileText,
        description: 'Read our articles',
      },
      {
        href: '/work',
        label: 'Work',
        icon: Briefcase,
        description: 'View our portfolio',
      },
      {
        href: '/about',
        label: 'About',
        icon: User,
        description: 'Learn about us',
      },
      {
        href: '/contact',
        label: 'Contact',
        icon: Mail,
        description: 'Get in touch',
      },
    ],
  },
  {
    id: 'discover',
    label: 'Discover',
    description: 'Additional content',
    items: [
      {
        href: '/registry',
        label: 'Registry',
        icon: Boxes,
        description: 'shadcn-compatible component registry',
      },
      {
        href: '/activity',
        label: 'Activity',
        icon: Activity,
        description: 'Recent updates',
      },
      {
        href: '/sponsors',
        label: 'Sponsors',
        icon: Heart,
        description: 'Support us',
      },
      {
        href: '/bookmarks',
        label: 'Bookmarks',
        icon: Bookmark,
        description: 'Your saved items',
      },
      { href: '/likes', label: 'Likes', icon: Heart, description: 'Content you liked' },
    ],
  },
];

// ============================================================================
// BOTTOM NAVIGATION (Mobile Only)
// ============================================================================

/**
 * Bottom navigation bar (mobile only)
 * Limited to 3-5 most important destinations
 */
export const BOTTOM_NAV: NavItem[] = [
  {
    href: '/',
    label: 'Home',
    icon: Home,
    exactMatch: true,
  },
  {
    href: '/ai',
    label: 'DCYFR AI',
    icon: Zap,
  },
  {
    href: '/blog',
    label: 'Blog',
    icon: FileText,
  },
  {
    href: '/work',
    label: 'Work',
    icon: Briefcase,
  },
  {
    href: '/contact',
    label: 'Contact',
    icon: Mail,
  },
];

// ============================================================================
// FOOTER NAVIGATION
// ============================================================================

/**
 * Footer navigation sections
 * Comprehensive sitemap for SEO and discoverability
 */
export const FOOTER_NAV_SECTIONS: NavSection[] = [
  {
    id: 'company',
    label: 'Company',
    description: 'Learn more about DCYFR Labs',
    items: [
      {
        href: '/about',
        label: 'About Us',
        description: 'Our story and team',
      },
      {
        href: '/sponsors',
        label: 'Sponsors',
        description: 'Support our work',
      },
      {
        href: '/contact',
        label: 'Contact',
        description: 'Get in touch',
      },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    description: 'Explore our content',
    items: [
      {
        href: '/blog',
        label: 'Blog',
        description: 'Latest articles',
      },
      {
        href: '/work',
        label: 'Portfolio',
        description: 'Our projects',
      },
      {
        href: '/ai',
        label: 'DCYFR AI',
        description: '@dcyfr/ai — open source agent for secure AI engineering',
      },
      {
        href: '/open-source',
        label: 'Open Source',
        description: 'Published npm packages',
      },
      {
        href: '/registry',
        label: 'Registry',
        description: 'shadcn-compatible component registry',
      },
      {
        href: '/activity',
        label: 'Activity',
        description: 'Recent updates',
      },
      {
        href: '/bookmarks',
        label: 'Bookmarks',
        description: 'Curated resources',
      },
      {
        href: '/likes',
        label: 'Likes',
        description: 'Content you liked',
      },
    ],
  },
  {
    id: 'resources',
    label: 'Resources',
    description: 'Helpful links and tools',
    items: [
      {
        href: '/feeds',
        label: 'Feeds',
        icon: Rss,
        description: 'Subscribe to updates',
      },
      {
        href: '/sitemap.xml',
        label: 'Sitemap',
        description: 'Site structure',
        external: true,
      },
    ],
  },
  {
    id: 'legal',
    label: 'Legal',
    description: 'Policies and terms',
    items: [
      {
        href: '/legal',
        label: 'Legal',
        description: 'Browse all legal policies',
      },
      {
        href: '/privacy',
        label: 'Privacy',
        description: 'How we handle your data',
      },
      {
        href: '/terms',
        label: 'Terms',
        description: 'Usage terms',
      },
      {
        href: '/security',
        label: 'Security',
        description: 'Security measures and reporting',
      },
      {
        href: '/accessibility',
        label: 'Accessibility',

        description: 'WCAG 2.1 AA compliance',
      },
      {
        href: '/acceptable-use',
        label: 'Acceptable Use',
        description: 'Usage guidelines',
      },
    ],
  },
];

// ============================================================================
// DEV TOOLS NAVIGATION (Development Only)
// ============================================================================
// COMPLETE NAVIGATION CONFIG
// ============================================================================

/**
 * Complete navigation configuration
 * Single source of truth for all navigation structures
 */
export const NAVIGATION: NavigationConfig = {
  header: HEADER_NAV,
  mobile: MOBILE_NAV_SECTIONS,
  bottom: BOTTOM_NAV,
  footer: FOOTER_NAV_SECTIONS,
  megaMenus: {
    blog: [
      {
        id: 'categories',
        label: 'Categories',
        items: BLOG_NAV,
      },
    ],
    work: [
      {
        id: 'portfolio',
        label: 'Portfolio',
        items: WORK_NAV,
      },
    ],
  },
};

// ============================================================================
// UTILITY FUNCTIONS (Re-exported from utils.ts)
// ============================================================================

// All utility functions are implemented in utils.ts and re-exported here for convenience
export { isNavItemActive, getAriaCurrent, getNavAnalytics, formatShortcut } from './utils';

/**
 * Get all keyboard shortcuts from navigation
 * Useful for command palette and help UI
 */
export function getKeyboardShortcuts(): Array<{
  shortcut: string;
  label: string;
  href: string;
  description?: string;
}> {
  return HEADER_NAV.filter((item): item is NavItem & { shortcut: string } => !!item.shortcut).map(
    (item) => ({
      shortcut: item.shortcut,
      label: item.label,
      href: item.href,
      description: item.description,
    })
  );
}

/**
 * Find navigation item by href
 * Searches across all navigation structures
 */
export function findNavItem(href: string): NavItem | undefined {
  const allItems = [
    ...HEADER_NAV,
    ...BLOG_NAV,
    ...WORK_NAV,
    ...BOTTOM_NAV,
    ...MOBILE_NAV_SECTIONS.flatMap((section) => section.items),
    ...FOOTER_NAV_SECTIONS.flatMap((section) => section.items),
  ];

  return allItems.find((item) => item.href === href);
}
